import React, { useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import Header from '../Common/Header';
import Sidebar from '../Common/Sidebar';
import './AttendanceDashboard.css';
import AttendanceView from './AttendanceView';
import EmployeesView from './EmployeesView';
import MessagesView from './MessagesView';
import TaskVerification from './TaskVerification';

const HRDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attendanceSort, setAttendanceSort] = useState('all');
  const { currentUser } = useAuth();
  const { users, tasks, attendance, exportAttendance, exportEmployees } = useData();

  const hrMenuItems = [
    { name: 'Overview', path: '/dashboard', icon: 'home' },
    { name: 'Employees', path: '/dashboard/employees', icon: 'users' },
    { name: 'Attendance', path: '/dashboard/attendance', icon: 'clock' },
    { name: 'Task Verification', path: '/dashboard/tasks', icon: 'check-square' },
    { name: 'Messages', path: '/dashboard/messages', icon: 'message-square' }
  ];

  const employees = users.filter(u => u.role === 'employee');
  const todayAttendance = attendance.filter(a => a.date === new Date().toISOString().split('T')[0]);
  const pendingTasks = tasks.filter(t => t.status === 'ongoing' || t.status === 'not_started');

  const DashboardHome = () => (
    <div className="dashboard-content">
      <div className="dashboard-headerr">
        <h2>Welcome back, {currentUser?.name}!</h2>
        <p>Manage employee records and attendance efficiently.</p>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="empinfo">
            <h3>{employees.length}</h3>
            <p>Total Employees</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="empinfo">
            <h3>{todayAttendance.length}</h3>
            <p>Present Today</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-tasks"></i>
          </div>
          <div className="empinfo">
            <h3>{pendingTasks.length}</h3>
            <p>Pending Tasks</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-percentage"></i>
          </div>
          <div className="empinfo">
            <h3>{employees.length > 0 ? Math.round((todayAttendance.length / employees.length) * 100) : 0}%</h3>
            <p>Attendance Rate</p>
          </div>
        </div>
      </div>

      <div className="dashboard-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button onClick={exportAttendance} className="btn btn-primary">
            <i className="fas fa-download"></i>
            Export Attendance
          </button>
          <button onClick={exportEmployees} className="btn btn-primary">
            <i className="fas fa-download"></i>
            Export Employees
          </button>
        </div>
      </div>

      <div className="dashboard-grid">
      <div className="dashboard-card">
  <div className="card-header-with-controls">
    <h3>Today's Attendance</h3>
    <div className="sort-controls">
      <select 
        value={attendanceSort} 
        onChange={(e) => setAttendanceSort(e.target.value)}
        className="sort-select"
      >
        <option value="all">All</option>
        <option value="present">Present</option>
        <option value="late">Late</option>
        <option value="absent">Absent</option>
      </select>
    </div>
  </div>
  
  <div className="attendance-list-container">
    <div className="attendance-list">
      {employees.length > 0 ? (
        employees
          .map(employee => {
            const attendanceRecord = todayAttendance.find(a => a.userId === employee.id);
            
            // Determine attendance status
            let status = 'absent';
            let checkIn = null;
            let checkOut = null;

            if (attendanceRecord) {
              checkIn = attendanceRecord.checkIn;
              checkOut = attendanceRecord.checkOut;

              if (checkIn) {
                const [time, period] = checkIn.includes('AM') || checkIn.includes('PM') 
                  ? checkIn.split(' ') 
                  : [checkIn, null];
                const [hours, minutes] = time.split(':').map(Number);
                let checkInHour = hours;

                // Convert to 24-hour format
                if (period) {
                  if (period === 'PM' && hours !== 12) checkInHour += 12;
                  if (period === 'AM' && hours === 12) checkInHour = 0;
                }

                const checkInMinutes = checkInHour * 60 + minutes;
                const cutoffTime = 9 * 60 + 15;

                status = checkInMinutes <= cutoffTime ? 'present' : 'late';
              }
            }

            return { ...employee, status, checkIn, checkOut };
          })
          .filter(employee => attendanceSort === 'all' || employee.status === attendanceSort)
          .map(employee => (
            <div key={employee.id} className="attendance-item">
              <div className="employee-info">
                <div className="employee-avatar">
                  {employee.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <h4>{employee.name}</h4>
                  {employee.checkIn ? (
                    <span>Check-in: {employee.checkIn}</span>
                  ) : (
                    <span>No check-in recorded</span>
                  )}
                </div>
              </div>
              <div className="attendance-status">
                <span className={`status-badge status-${employee.status}`}>
                  {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                </span>
                {employee.checkOut && <span className="checkout-time">Out: {employee.checkOut}</span>}
              </div>
            </div>
          ))
      ) : (
        <p className="no-data">No employees found</p>
      )}
    </div>
  </div>
</div>

        <div className="dashboard-card">
          <h3>Task Status Overview</h3>
          <div className="task-status-chart">
            <div className="status-item">
              <div className="status-color status-completed"></div>
              <span>Completed: {tasks.filter(t => t.status === 'completed').length}</span>
            </div>
            <div className="status-item">
              <div className="status-color status-ongoing"></div>
              <span>Ongoing: {tasks.filter(t => t.status === 'ongoing').length}</span>
            </div>
            <div className="status-item">
              <div className="status-color status-not-started"></div>
              <span>Not Started: {tasks.filter(t => t.status === 'not_started').length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-layout">
      <Sidebar 
        menuItems={hrMenuItems}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="dashboard-main">
        <Header 
          title="HR Dashboard"
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="dashboard-body">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/employees" element={<EmployeesView />} />
            <Route path="/attendance" element={<AttendanceView />} />
            <Route path="/tasks" element={<TaskVerification />} />
            <Route path="/messages" element={<MessagesView />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default HRDashboard;
